import React, { useState, useEffect, useRef, useCallback } from "react";
import classNames from "classnames";
import {
  TableContent,
  TextRenderer,
  TableContainer,
  IconChevronLeft,
  TextButton,
  PanelContent,
  Panel,
  SimpleTable,
  SimpleTableRow,
  SimpleTableWrapper,
  SimpleTableBody,
  SimpleTableData,
  DropdownMenu,
  usePreventTableScrollingRef,
  Input,
  INPUT_TEXT_POSITIONS,
  CELL_CLASSES,
  SimpleTableHead,
  SimpleTableHeader,
} from "cdk-radial";
import { AgGridReact } from "ag-grid-react";
import { cleanName } from "../../utils/spec-utils";
import { API_VERSION_STATUS } from "./constants";
import styled, { css } from "styled-components";

const isGeneralRelease = (status) =>
  status === API_VERSION_STATUS.GENERAL_RELEASE || API_VERSION_STATUS.BETA || status == null;

const getProdInstances = (apiInfo) => {
  const data = apiInfo?.data;
  if (!data) return [];

  // Shape A: environments[...].instances
  if (Array.isArray(data.environments) && data.environments.length) {
    const prodEnv =
      data.environments.find((e) => (e?.name || e?.environment) === "prod") ||
      {};
    const list = prodEnv.instances || [];
    return list.filter((i) => isGeneralRelease(i?.lifecycleStatus));
  }

  // Shape B: instances[...]
  if (Array.isArray(data.instances) && data.instances.length) {
    return data.instances.filter(
      (i) =>
        (i?.environment === "prod" || i?.environment?.name === "prod")
    );
  }

  return [];
};

const getProdInstanceByVersion = (apiInfo, version) => {
  const prodInstances = getProdInstances(apiInfo);
  return prodInstances.find((i) => i?.version === version);
};

const safeParseSpec = async (spec) => {
  try {
    if (typeof spec === "string") return JSON.parse(spec);
    return spec || {};
  } catch (err) {
    console.error("Error parsing spec", err);
    return {};
  }
};

const parseSpec = async (spec) => {
  try {
    if (typeof spec === "string") {
      return JSON.parse(spec);
    }
    return spec || {};
  } catch (err) {
    console.error("Error parsing spec", err);
    return {};
  }
};

const generateEndpointList = (schema) => {
  if (!schema?.paths) return [];
  const endpointList = [];
  for (const [path, operations] of Object.entries(schema.paths)) {
    for (const [verb, operation] of Object.entries(operations)) {
      endpointList.push({
        method: verb.toUpperCase(),
        endpoint: cleanName(operation.operationId || path),
        path: path,
        key: `${verb}-${path}`,
        calls: "",
      });
    }
  }
  return endpointList;
};
const validateNumericInput = (value) => {
  // Remove any non-numeric characters & leading zeros
  const cleaned = value.replace(/^0+(?=\d)/, '');
  return cleaned === '' ? 0 : cleaned;
};

const PricingCalculator = ({ onCompareNowClick, apiInfo, apiDetailsOnClickHandlers }) => {
  const { trackSelectApiVersion, trackAdjustedCalls } = apiDetailsOnClickHandlers || {};
  const asyncTypingTimeoutRef = useRef(null);
  const asyncLastTrackedValueRef = useRef(0);

  useEffect(() => {
    return () => {
      if (asyncTypingTimeoutRef.current) {
        clearTimeout(asyncTypingTimeoutRef.current);
      }
    };
  }, []);

  const agGridThemeClass = "ag-theme-balham";
  const tableClass = classNames(agGridThemeClass);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [endpoints, setEndpoints] = useState([]);
  const [versions, setVersions] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [eventsPerMonth, setEventsPerMonth] = useState(0);

  const selectedVersionRef = useRef(selectedVersion);
  useEffect(() => {
    selectedVersionRef.current = selectedVersion;
  }, [selectedVersion]);

  const triggerAsyncTracking = (newValue) => {
    if (String(newValue) !== String(asyncLastTrackedValueRef.current)) {
      if (trackAdjustedCalls) {
        trackAdjustedCalls("Events per Month", newValue, selectedVersionRef.current || "");
      }
      asyncLastTrackedValueRef.current = newValue;
    }
  };

  const tableContentRef = usePreventTableScrollingRef();
  const frameworkComponentsExtended = { textRenderer: TextRenderer };

  const gridRef = useRef();

  const calculateOverageForAPI = useCallback(() => {
    if (
      apiInfo?.data?.kind !== "api" ||
      !gridRef.current?.api ||
      !selectedVersion
    )
      return;

    // current prod instance (both shapes)
    const currentInstance = getProdInstanceByVersion(apiInfo, selectedVersion);
    if (!currentInstance?.id) return;
    const currentInstanceId = currentInstance.id;

    // collect current grid rows (user-entered calls)
    const allNodes = [];
    gridRef.current.api.forEachNode((node) => allNodes.push(node.data));

    setTableData((prev) =>
      prev.map((plan) => {
        let totalOverageFee = 0;

        (plan.containers || []).forEach((container) => {
          if (container.isFree) return;

          const containerEndpoints = container.endPoints || [];
          let containerTotalCalls = 0;

          containerEndpoints.forEach((endpointInfo) => {
            const belongsToCurrentInstance = endpointInfo.apiInstanceId
              ? endpointInfo.apiInstanceId === currentInstanceId
              : true;

            if (!belongsToCurrentInstance) return;

            const gridEndpoint = allNodes.find((node) => {
              const ep = endpoints.find(
                (e) =>
                  e.path === endpointInfo.route &&
                  e.method.toLowerCase() ===
                    (endpointInfo.verb || "").toLowerCase()
              );
              return ep?.key === node?.key;
            });

            if (gridEndpoint) {
              const calls = parseInt(gridEndpoint.calls, 10) || 0;
              containerTotalCalls += calls;
            }
          });

          const quota = container.quota || 0;
          const overageRate = container.overage || 0;

          if (containerTotalCalls > quota) {
            const overageCalls = containerTotalCalls - quota;
            totalOverageFee += overageCalls * overageRate;
          }
        });

        return {
          ...plan,
          calculatedOverage: parseFloat((totalOverageFee || 0).toFixed(2)),
        };
      })
    );
  }, [apiInfo, endpoints, selectedVersion]);

  const CellsPerMonth = React.forwardRef((cellProps, ref) => {
    const [value, setValue] = useState(cellProps.value ?? 0);
    const inputRef = useRef(null);
    const typingTimeoutRef = useRef(null);
    const lastTrackedValueRef = useRef(cellProps.value ?? 0);
    const isDirtyRef = useRef(false);
    const paramsRef = useRef(cellProps);

    // Keep paramsRef up to date with any React-driven renders
    paramsRef.current = cellProps;

    const endpointName = paramsRef.current.data?.endpoint || '';

    React.useImperativeHandle(ref, () => ({
      refresh(params) {
        paramsRef.current = params;
        setValue(params.value ?? 0);
        if (!isDirtyRef.current) {
          lastTrackedValueRef.current = params.value ?? 0;
        }
        return true;
      }
    }));

    const triggerTracking = (newValue) => {
      if (!isDirtyRef.current) return;
      if (String(newValue) !== String(lastTrackedValueRef.current)) {
        if (trackAdjustedCalls) {
          trackAdjustedCalls(endpointName, newValue, selectedVersionRef.current);
        }
        lastTrackedValueRef.current = newValue;
      }
      isDirtyRef.current = false;
    };

    useEffect(() => {
      return () => {
        if (typingTimeoutRef.current) {
          clearTimeout(typingTimeoutRef.current);
        }
      };
    }, []);

    useEffect(() => {
      // Only focus the first row's input
      if (value !== "" || paramsRef.current.rowIndex === 0) {
        const timer = setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.focus();
          }
        }, 10);
        return () => clearTimeout(timer);
      }
    }, [value]);
    
    const handleChange = (e) => {
      isDirtyRef.current = true;
      const inputValue = e.target.value;
      const cleanedValue = validateNumericInput(inputValue);
      setValue(cleanedValue);
      paramsRef.current.setValue?.(cleanedValue);

      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      typingTimeoutRef.current = setTimeout(() => {
        triggerTracking(cleanedValue);
      }, 1000);

      // Trigger overage calculation after value change
      setTimeout(() => {
        if (apiInfo?.data?.kind === "api") {
          calculateOverageForAPI();
        }
      }, 100);
    };

    const handleKeyPress = (e) => {
      // Prevent non-numeric characters from being entered
      if (!/[0-9]/.test(e.key) && !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(e.key)) {
        e.preventDefault();
      }
    };

    return (
      <div style={{ maxWidth: "124px" }}>
        <Input
          ref={inputRef}
          dataTestId="input"
          hideBorder="none"
          textAlign={INPUT_TEXT_POSITIONS.CENTER}
          placeholder="0"
          size="small"
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyPress}
          onBlur={(e) => {
            if (typingTimeoutRef.current) {
              clearTimeout(typingTimeoutRef.current);
            }
            const cleanedValue = validateNumericInput(e.target.value);
            triggerTracking(cleanedValue);
          }}
          onInputClear={() => {
            isDirtyRef.current = true;
            setValue("");
            if (typingTimeoutRef.current) {
              clearTimeout(typingTimeoutRef.current);
            }
            triggerTracking(0);
            setTimeout(() => {
              if (apiInfo?.data?.kind === "api") {
                calculateOverageForAPI();
              }
            }, 100);
          }}
          style={{ textAlign: "center" }}
        />
      </div>
    );
  });

  const columnDefs = [
    {
      cellRendererFramework: CellsPerMonth,
      field: "calls",
      headerName:
        apiInfo?.data?.kind === "async-api"
          ? "Events per Month"
          : "Calls per Month",
      width: 130,
      sortable: true,
      resizable: true,
    },
    ...(apiInfo?.data?.kind !== "async-api"
      ? [
          {
            cellRendererFramework: TextRenderer,
            cellClass: [CELL_CLASSES.ALIGN_CELL_CENTER],
            field: "endpoint",
            headerName: "Endpoint Name",
            width: 360,
            resizable: true,
            sortable: true,
          },
        ]
      : []),
  ];

  const handleVersionChange = async (version) => {
    setSelectedVersion(version);

    // reset grid + overage numbers
    if (gridRef.current?.api) {
      gridRef.current.api.forEachNode((node) => node.setDataValue("calls", ""));
    }
    setTableData((prev) => prev.map((p) => ({ ...p, calculatedOverage: 0 })));

    // prod instances only, across both shapes
    const instance = getProdInstanceByVersion(apiInfo, version);
    if (!instance) return;

    const specContent = await safeParseSpec(instance.spec);
    const locEndpoints = generateEndpointList(specContent);
    setEndpoints(locEndpoints);
  };

  useEffect(() => {
    let instances = [];
    if (apiInfo?.data?.environments?.length) {
      const prodEnv = apiInfo.data.environments.find(
        (env) => env.name === "prod"
      );
      if (prodEnv?.instances?.length) {
        instances = prodEnv.instances.filter(
          (inst) =>
            inst.lifecycleStatus === API_VERSION_STATUS.GENERAL_RELEASE ||
            inst.lifecycleStatus === null
        );
      }
    } else if (apiInfo?.data?.instances) {
      instances = apiInfo.data.instances.filter(
        (inst) =>
          inst.environment === "prod"
      );
    }

    if (instances?.length) {
      const versionList = instances.map((inst) => inst.version);
      setVersions(versionList);
      setSelectedVersion(versionList[0]);
      handleVersionChange(versionList[0]);
    }
  }, [apiInfo]);

  useEffect(() => {
    const prodInstances = getProdInstances(apiInfo);
    if (prodInstances?.length) {
      const versionList = prodInstances.map((i) => i.version);
      setVersions(versionList);
      setSelectedVersion(versionList[0]);
      handleVersionChange(versionList[0]);
    }
  }, [apiInfo]);

  const handleGridReady = useCallback(() => {
    gridRef.current?.api?.sizeColumnsToFit();
  }, []);
  const useSizeColumnsToFitOnResize = (gridRef) => {
    useEffect(() => {
      const onResizeHandler = () => {
        if (gridRef.current.api === null) return;
        gridRef.current.api.sizeColumnsToFit();
      };
      window.addEventListener("resize", onResizeHandler);
      return () => window.removeEventListener("resize", onResizeHandler);
    }, [gridRef]);
  };
  useSizeColumnsToFitOnResize(gridRef);
  const onCellValueChanged = useCallback(() => {
    if (apiInfo?.data?.kind === "api") {
      calculateOverageForAPI();
    }
  }, [calculateOverageForAPI, apiInfo]);

  const gridRowData =
    apiInfo?.data?.kind === "async-api" ? [{ calls: "" }] : endpoints;

  useEffect(() => {
    const formatted = apiInfo?.data?.plans.map((plan) => ({
      id: plan.id,
      name: plan.name || plan?.plan,
      price: plan.price,
      overage: plan.containers?.[0]?.overage ?? 0,
      type: plan.type,
      quota: plan.containers?.[0]?.quota ?? 0,
      containers: plan.containers || [],
      calculatedOverage: 0,
    }));
    formatted.sort((a, b) => a.price - b.price);
    setTableData(formatted);
  }, [apiInfo]);

  const handleEventsPerMonthChange = (e) => {
    const inputValue = e.target.value;
    const cleanedValue = validateNumericInput(inputValue);
    setEventsPerMonth(cleanedValue);

    if (asyncTypingTimeoutRef.current) {
      clearTimeout(asyncTypingTimeoutRef.current);
    }
    asyncTypingTimeoutRef.current = setTimeout(() => {
      triggerAsyncTracking(cleanedValue);
    }, 1000);
  };

  const handleEventsPerMonthKeyPress = (e) => {
    // Prevent non-numeric characters from being entered
    if (!/[0-9]/.test(e.key) && !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(e.key)) {
      e.preventDefault();
    }
  };

  return (
    <div>
      <div>
        <TextButton
          icon={<IconChevronLeft />}
          onClick={onCompareNowClick}
          text="Back to Pricing"
        />
      </div>
      <div className="api-dir-pkg_pricing-calculator-container">
        <div className="api-dir-pkg_pricing-calculator-title">
          <div className="api-dir-pkg_pricing-calculator-text">
            Pricing Calculator
          </div>
          <div className="api-dir-pkg_pricing-calculator-description">
            {" "}
            Fill in your events per month to calculate your estimated cost.
          </div>
          <div className="api-dir-pkg_pricing-calculator-caption">
            The cost estimates generated by this tool are for informational and illustrative purposes
            only. They are not a guarantee of actual cost for the services.
          </div>
        </div>
        <div className="api-dir-pkg_pricing-calculator-section">
          <div
            className="api-dir-pkg_pricing-calculator-content"
            style={{
              height: apiInfo?.data?.kind !== "async-api" ? "1060px" : "509px",
            }}
          >
            <div className="api-dir-pkg_pricing-calculator-table">
              <Panel>
                <PanelContent>
                  <SimpleTableWrapper className="api-dir-pkg_price-calc-table-wrapper">
                    <SimpleTable className="api-dir-pkg_price-calc-table">
                      <SimpleTableHead className="api-dir-pkg_price-calc-table">
                        <SimpleTableRow className="api-dir-pkg_price-calc-table-row-plans">
                          <SimpleTableData
                            textAlign="left"
                            className="data-table"
                          ></SimpleTableData>
                          {tableData.map((plan) => (
                            <SimpleTableData
                              textAlign="center"
                              className="data-table"
                              key={plan.id}
                            >
                              {plan.name.charAt(0).toUpperCase() +
                                plan.name.slice(1)}
                            </SimpleTableData>
                          ))}
                        </SimpleTableRow>
                      </SimpleTableHead>
                      <SimpleTableBody>
                        <SimpleTableRow className="api-dir-pkg_price-calc-table-row">
                          <SimpleTableData
                            textAlign="left"
                            className="data-table"
                          >
                            Monthly Subscription Fee
                          </SimpleTableData>
                          {tableData.map((plan) => (
                            <SimpleTableData
                              textAlign="center"
                              key={plan.id}
                              className="data-table"
                            >
                              ${plan.price.toLocaleString()}
                            </SimpleTableData>
                          ))}
                        </SimpleTableRow>
                        <SimpleTableRow className="api-dir-pkg_price-calc-table-row">
                          <SimpleTableData
                            textAlign="left"
                            className="data-table"
                          >
                            Estimated Overage Fees
                          </SimpleTableData>
                          {tableData.map((plan) => (
                            <SimpleTableData
                              textAlign="center"
                              className="data-table"
                              key={plan.id}
                            >
                              $
                              {(apiInfo?.data?.kind === "async-api"
                                ? eventsPerMonth > plan.quota
                                  ? (eventsPerMonth - plan.quota) * plan.overage
                                  : 0
                                : plan.calculatedOverage || 0
                              ).toLocaleString()}
                            </SimpleTableData>
                          ))}
                        </SimpleTableRow>
                        <SimpleTableRow className="api-dir-pkg_price-calc-table-row-estimated-price">
                          <SimpleTableData
                            textAlign="left"
                            className="data-table"
                          >
                            Estimated Total
                          </SimpleTableData>
                          {tableData.map((plan) => (
                            <SimpleTableData
                              textAlign="center"
                              className="data-table"
                              key={plan.id}
                            >
                              $
                              {(
                                plan.price +
                                (apiInfo?.data?.kind === "async-api"
                                  ? eventsPerMonth > plan.quota
                                    ? (eventsPerMonth - plan.quota) *
                                      plan.overage
                                    : 0
                                  : plan.calculatedOverage || 0)
                              ).toLocaleString()}
                            </SimpleTableData>
                          ))}
                        </SimpleTableRow>
                      </SimpleTableBody>
                    </SimpleTable>
                  </SimpleTableWrapper>
                </PanelContent>
              </Panel>
            </div>
            <div className="api-dir-pkg_pricing-table-border"></div>
            <div>
              {apiInfo.data?.kind === "async-api" && (
                <div className="api-dir-pkg_price-calc-events">
                  <SimpleTableWrapper
                    style={{
                      border: "none",
                    }}
                    className="api-dir-pkg_price-calc-wrapper-events"
                  >
                    <SimpleTable>
                      <SimpleTableHead>
                        <SimpleTableRow>
                          <SimpleTableHeader
                            textAlign="left"
                            colSpan="1"
                            style={{ margin: "10px 0", width: "100px" }}
                          >
                            Events per Month
                          </SimpleTableHeader>
                        </SimpleTableRow>
                      </SimpleTableHead>
                      <SimpleTableBody>
                        <SimpleTableRow>
                          <SimpleTableData textAlign="center">
                            <Input
                              className="api-dir-pkg_pricing-input"
                              dataTestId="input"
                              hideBorder="none"
                              iconPosition="trailing"
                              inputDivider="left"
                              type="text"
                              value={eventsPerMonth}
                              onChange={handleEventsPerMonthChange}
                              onKeyDown={handleEventsPerMonthKeyPress}
                              onBlur={(e) => {
                                if (asyncTypingTimeoutRef.current) {
                                  clearTimeout(asyncTypingTimeoutRef.current);
                                }
                                const cleanedValue = validateNumericInput(e.target.value);
                                triggerAsyncTracking(cleanedValue);
                              }}
                              size="standard"
                            />
                          </SimpleTableData>
                        </SimpleTableRow>
                      </SimpleTableBody>
                    </SimpleTable>
                  </SimpleTableWrapper>
                </div>
              )}
            </div>
            {apiInfo.data?.kind !== "async-api" && (
              <div className="api-dir-pkg_price-calc-wrapper">
                <div className="api-dir-pkg_price-calc-version">
                  <DropdownMenu
                    inputId="api-version"
                    isRequired={true}
                    label="API Version"
                    placeholder="Select Version"
                    value={
                      selectedVersion
                        ? {
                            label: `Version ${selectedVersion}`,
                            value: selectedVersion,
                          }
                        : null
                    }
                    onChange={(e) => {
                      handleVersionChange(e.value);
                      if (trackSelectApiVersion) {
                        trackSelectApiVersion(e.value);
                      }
                    }}
                    options={versions.map((v) => ({
                      label: `Version ${v}`,
                      value: v,
                    }))}
                  />
                </div>
                <div className="api-dir-pkg_price-calc-grid">
                  <TableContainer className={tableClass} tableLayout="normal">
                    <TableContent
                      data-testid="table-content"
                      ref={tableContentRef}
                      className="api-dir-pkg_pricing-table-content"
                      style={{ height: "400px" }}
                    >
                      <div className="api-dir-pkg_pricing-aggrid-table">
                        <AgGridReact
                          ref={gridRef}
                          defaultColDef={{ sortable: true }}
                          style={{ height: "400px" }}
                          frameworkComponents={frameworkComponentsExtended}
                          columnDefs={columnDefs}
                          rowData={gridRowData}
                          onGridReady={handleGridReady}
                          onCellValueChanged={onCellValueChanged}
                          rowHeight={72}
                          resizable={true}
                        />
                      </div>
                    </TableContent>
                  </TableContainer>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingCalculator;
